import moment from 'moment';
import { getPeriodDateFromUntil } from '@/shared/components/PeriodButtonGroup';
export const ageBuckets = ['notDue', 'd1to30', 'd31to60', 'd61to90', 'd90plus'];
const bucketBounds = {
    notDue: { from: null, to: -1 },
    d1to30: { from: 0, to: 30 },
    d31to60: { from: 31, to: 60 },
    d61to90: { from: 61, to: 90 },
    d90plus: { from: 91, to: null },
};
// An invoice due today already counts as overdue, like on the billing overview.
export const getAgeBucket = (daysOverdue) => {
    if (daysOverdue < 0)
        return 'notDue';
    if (daysOverdue <= 30)
        return 'd1to30';
    if (daysOverdue <= 60)
        return 'd31to60';
    if (daysOverdue <= 90)
        return 'd61to90';
    return 'd90plus';
};
export const daysOverdue = (expiryDate, asOf) => expiryDate
    ? moment(asOf)
        .startOf('day')
        .diff(moment(expiryDate).startOf('day'), 'days')
    : 0;
// Infinite queries over PagingResult pages: request the next page while fewer rows than the total are loaded.
export const nextPageParam = (lastPage, pages) => pages.reduce((count, page) => count + page.data.length, 0) < lastPage.total
    ? pages.length
    : undefined;
export const sum = (values) => values.reduce((total, value) => total + value, 0);
export const emailStatusKey = (status) => {
    switch (status) {
        case 2:
            return 'delivered';
        case 3:
            return 'opened';
        case 4:
            return 'deliveryFailed';
        default:
            return 'sent';
    }
};
export const emailStatusValue = (key) => {
    switch (key) {
        case 'delivered':
            return 2;
        case 'opened':
            return 3;
        case 'deliveryFailed':
            return 4;
        default:
            return 1;
    }
};
export const emailStatusKeys = ['sent', 'delivered', 'opened', 'deliveryFailed'];
const reminderStatusValue = {
    all: 0,
    none: 1,
    reminded: 2,
    failed: 3,
    paused: 4,
};
export const offsetLabel = (days) => `${days > 0 ? '+' : ''}${days}d`;
export const ruleLabel = (rule) => {
    if (!rule || rule.dueDateOffsetDays === null || rule.dueDateOffsetDays === undefined) {
        return (rule === null || rule === void 0 ? void 0 : rule.templateName) || '';
    }
    const offset = offsetLabel(rule.dueDateOffsetDays);
    return rule.templateName ? `${rule.templateName} · ${offset}` : offset;
};
export const overdueInfoParams = (filters) => ({
    relationId: filters.relationId || undefined,
    ownCompanies: filters.companies.length ? filters.companies : undefined,
    invoiceNr: filters.search || undefined,
});
// The backend filters on the due date, so the selected age bucket becomes a due-date window. With no
// bucket selected only overdue invoices (due today or earlier) are listed.
export const overdueListParams = (filters) => {
    const selected = filters.ageBuckets.map(bucket => bucketBounds[bucket]);
    const lowerBounds = selected.map(x => x.from);
    const upperBounds = selected.map(x => x.to);
    const daysOverdueFrom = selected.length
        ? lowerBounds.some(x => x === null)
            ? undefined
            : Math.min(...lowerBounds)
        : 0;
    const daysOverdueTo = selected.length && !upperBounds.some(x => x === null)
        ? Math.max(...upperBounds)
        : undefined;
    const today = moment().startOf('day');
    return Object.assign(Object.assign({}, overdueInfoParams(filters)), { reminderStatus: reminderStatusValue[filters.reminderStatus], expiryDateTo: daysOverdueFrom !== undefined
            ? today
                .clone()
                .subtract(daysOverdueFrom, 'days')
                .format('YYYY-MM-DD')
            : undefined, expiryDateFrom: daysOverdueTo !== undefined
            ? today
                .clone()
                .subtract(daysOverdueTo, 'days')
                .format('YYYY-MM-DD')
            : undefined });
};
export const historyParams = (filters) => {
    let from = filters.dateFrom ? moment(filters.dateFrom) : null;
    let until = filters.dateUntil ? moment(filters.dateUntil) : null;
    if (filters.period && filters.period !== 'all') {
        const { dateFrom, dateUntil } = getPeriodDateFromUntil(filters.period);
        from = dateFrom ? moment(dateFrom) : null;
        until = dateUntil ? moment(dateUntil) : null;
    }
    return {
        dateFrom: from ? from.format('YYYY-MM-DD') : undefined,
        dateTo: until ? until.format('YYYY-MM-DD') : undefined,
        relationId: filters.relationId || undefined,
        reminderId: filters.reminderId || undefined,
        emailStatus: filters.emailStatus === 'all' ? undefined : [emailStatusValue(filters.emailStatus)],
        source: filters.source === 'automatic' ? 1 : filters.source === 'manual' ? 2 : 0,
    };
};
export const groupRemindersByInvoice = (invoices) => new Map(invoices.map(invoice => [invoice.id, invoice.reminders || []]));
export const ageingFromInfo = (info) => [
    { bucket: 'notDue', amount: info.notDueAmount, count: info.notDueCount },
    { bucket: 'd1to30', amount: info.overdue1To30Amount, count: info.overdue1To30Count },
    { bucket: 'd31to60', amount: info.overdue31To60Amount, count: info.overdue31To60Count },
    { bucket: 'd61to90', amount: info.overdue61To90Amount, count: info.overdue61To90Count },
    { bucket: 'd90plus', amount: info.overdue90PlusAmount, count: info.overdue90PlusCount },
];
export const groupPerRelation = (invoices, remindersByInvoice, upcoming) => {
    const groups = new Map();
    invoices.forEach(invoice => {
        const key = `${invoice.customer.id}|${invoice.companyId}`;
        const group = groups.get(key) || {
            key,
            relation: invoice.customer,
            companyId: invoice.companyId,
            companyName: invoice.companyName,
            invoices: [],
            unpaidAmount: 0,
            oldestDaysOverdue: Number.MIN_SAFE_INTEGER,
            reminders: [],
            next: null,
            autosend: invoice.reminderAutosend,
        };
        group.invoices.push(invoice);
        group.unpaidAmount += invoice.dueAmount;
        group.oldestDaysOverdue = Math.max(group.oldestDaysOverdue, daysOverdue(invoice.expiryDate));
        const next = upcoming.get(invoice.id);
        if (next && (!group.next || next.date < group.next.date))
            group.next = next;
        if (invoice.reminderAutosend !== 0)
            group.autosend = invoice.reminderAutosend;
        groups.set(key, group);
    });
    return Array.from(groups.values())
        .map(group => {
        const seen = new Set();
        const groupReminders = group.invoices
            .flatMap(x => remindersByInvoice.get(x.id) || [])
            .filter(r => {
            const mailKey = r.emailId || r.id;
            if (seen.has(mailKey))
                return false;
            seen.add(mailKey);
            return true;
        })
            .sort((a, b) => a.sentDate.localeCompare(b.sentDate));
        return Object.assign(Object.assign({}, group), { reminders: groupReminders });
    })
        .sort((a, b) => b.unpaidAmount - a.unpaidAmount);
};
export const upcomingPerDay = (upcoming, days) => {
    const horizon = moment()
        .startOf('day')
        .add(days, 'days');
    const byDate = new Map();
    upcoming
        .filter(x => moment(x.date).isSameOrBefore(horizon))
        .forEach(x => {
        const date = moment(x.date).format('YYYY-MM-DD');
        byDate.set(date, [...(byDate.get(date) || []), x]);
    });
    return Array.from(byDate.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([date, list]) => ({
        date,
        invoices: list.sort((a, b) => b.unpaidAmount - a.unpaidAmount),
        amount: sum(list.map(x => x.unpaidAmount)),
        relationCount: new Set(list.map(x => x.customer.id)).size,
    }));
};
export const reminderSourceLabel = (reminder, intl, messages) => {
    if (reminder.reminderId)
        return `${intl.formatMessage(messages.Automatic)} · ${ruleLabel(reminder)}`;
    if (reminder.sentBySystem)
        return intl.formatMessage(messages.NightlyRun);
    return intl.formatMessage(messages.ManualBy, { user: reminder.sentBy });
};
